import React, { useState } from 'react';
import { ChevronRight, X, Zap } from 'lucide-react';

/**
 * Guided Tour Component
 * Shows new users how to quickly generate and copy replies
 */
function GuidedTour({ onComplete, onSkip }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: '🎯 Welcome to RepuGate',
      description: 'Get 3 AI-powered reply options for each review in seconds',
      hint: 'Let\'s walk through the process quickly (2 min)'
    },
    {
      title: '📝 See Your Reviews',
      description: 'Your Facebook reviews are loaded below. Click any review to generate replies.',
      targetElement: '[data-tour="review-list"]'
    },
    {
      title: '🎨 Choose Your Tone',
      description: 'Want professional? Warm? Apologetic? Select your preferred tone before generating.',
      targetElement: '[data-tour="tone-selector"]'
    },
    {
      title: '⚡ Generate Options',
      description: 'Click "Generate Replies" to get 3 unique reply options powered by AI.',
      targetElement: '[data-tour="generate-button"]'
    },
    {
      title: '📋 Copy & Paste',
      description: 'Pick your favorite option, copy it, and manually paste it on Facebook (one-click posting coming soon).',
      targetElement: '[data-tour="reply-options"]'
    },
    {
      title: '🎉 You\'re Ready!',
      description: 'Start replying to your reviews. Your generated replies are saved for reference.',
      hint: 'Pro tip: Bulk generate replies for your top 5 reviews at once!\n\nKeyboard shortcuts:\n• N = Next review\n• R = Regenerate with different tone\n• C = Copy selected reply'
    }
  ];

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 p-2 hover:bg-slate-700/50 rounded-lg transition"
          title="Skip tour"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Step indicator */}
        <div className="flex gap-1 mb-6">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                idx <= step ? 'bg-purple-500' : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="space-y-4 mb-8">
          <div className="flex items-start gap-3">
            <Zap className="w-6 h-6 text-pink-400 mt-1 flex-shrink-0" />
            <h2 className="text-2xl font-bold text-white">{currentStep.title}</h2>
          </div>

          <p className="text-slate-300 text-lg leading-relaxed">
            {currentStep.description}
          </p>

          {currentStep.hint && (
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4 text-sm text-purple-200 whitespace-pre-line">
              {currentStep.hint}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          {step > 0 && (
            <button
              onClick={handleBack}
              className="px-4 py-2 text-slate-300 border border-slate-600 rounded-lg hover:bg-slate-700/30 transition"
            >
              ← Back
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center justify-center gap-2"
          >
            {isLastStep ? 'Get Started' : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Skip option */}
        <button
          onClick={onSkip}
          className="w-full mt-3 py-2 text-sm text-slate-400 hover:text-slate-300 transition"
        >
          Skip tour
        </button>
      </div>
    </div>
  );
}

export default GuidedTour;
